import React from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileCheck, Shield, Search, Monitor, Briefcase, Building2, TrendingUp, CheckCircle } from 'lucide-react';

const ServicesPage = () => {
  const location = useLocation();

  useEffect(() => {
     // Add scroll padding globally for this page
    document.documentElement.style.scrollPaddingTop = '80px';
        
    // Scroll to section if hash exists
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      // Scroll to top if no hash
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Cleanup
    return () => {
      document.documentElement.style.scrollPaddingTop = '0px';
    };
  }, [location]);
  
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  const services = [
    {
      id: 'external-audit',
      icon: FileCheck,
      title: 'External Auditing and Assurance',
      subtitle: 'Delivering Credible and Transparent Financial Reporting',
      description: 'We conduct statutory audits in both the public and private sectors, with extensive experience ensuring our auditing teams are skilled in the distinct requirements of each sector.',
      bullets: [
        'Compliance with latest regulations',
        'Partner-led audit teams with Big Four alumni',
        'Expert team with proven track record',
        'Tailored solutions for your needs',
      ],
      cardHeading: 'Why Choose OMC',
      cardIntro: 'Our team ensures credible, transparent, and compliant financial reporting tailored to your organisation\'s needs.',
      iconColor: '#000565',
      bgColor: '#FFFFFF',
      sectionBg: '#FFFFFF',
    },
    {
      id: 'internal-audit',
      icon: Shield,
      title: 'Internal Audit Services',
      subtitle: 'Strengthening Governance Through Effective Internal Controls',
      description: 'The increased awareness of good corporate governance, effective risk management, and internal controls has created growing demand for quality internal audit services.',
      bullets: [
        'Full internal audit function',
        'Audit committee advisory expertise',
        'Integrated risk management frameworks',
        'Performance information audit specialists',
      ],
      cardHeading: 'Our Distinct Advantage',
      cardIntro: 'We provide end-to-end internal audit solutions that strengthen governance and risk management frameworks.',
      iconColor: '#0383ED',
      bgColor: '#FFFFFF',
      sectionBg: '#F4F7F9',
    },
    {
      id: 'forensic',
      icon: Search,
      title: 'Forensic Audit and Investigations',
      subtitle: 'Detecting, Preventing and Responding to Financial Crime',
      description: 'Our specialised forensic audits and investigations are designed to detect, prevent, and respond to fraud, misconduct, and general non-compliance.',
      bullets: [
        'Criminal and civil litigation support experience',
        'Advanced data mining and analytics capabilities',
        'Evidence-based investigation methodology',
        'Expert witness testimony for legal proceedings',
      ],
      cardHeading: 'What Sets Us Apart',
      cardIntro: 'We uncover, prevent, and respond to financial misconduct with evidence-based investigations.',
      iconColor: '#2E70F0',
      bgColor: '#FFFFFF',
      sectionBg: '#FFFFFF',
    },
    {
      id: 'it-risk',
      icon: Monitor,
      title: 'IT Risk, Assurance and Advisory Services',
      subtitle: 'Managing Technology Risks for Business Success',
      description: 'Our focus is assisting organisations to address risks and performance relating to information technology use.',
      bullets: [
        'Integrated IT governance frameworks',
        'Latest data interrogation and analytics tools',
        'End-to-end controls from strategy to security',
        'Technology performance and value realization',
      ],
      cardHeading: 'Our Competitive Edge',
      cardIntro: 'We help organisations manage technology risks and optimise IT performance for measurable business value.',
      iconColor: '#06B6D4',
      bgColor: '#FFFFFF',
      sectionBg: '#F4F7F9',
    },
  ];

    const moreServices = [
    {
      id: 'consulting',
      icon: Briefcase,
      title: 'Consulting Services',
      subtitle: 'Comprehensive Solutions for Growth and Excellence',
      description: 'Our consulting services provide integrated business and investment solutions designed to accelerate growth, enhance performance, and strengthen organisational capability.',
      bullets: [
        'Big Four consulting experience',
        'Strategic advisory with execution support',
        'Customised solutions for unique business contexts',
        'Long-term partnership beyond project delivery',
      ],
      cardHeading: 'Why Choose OMC',
      cardIntro: 'We provide practical consulting solutions that accelerate growth and enhance organisational performance.',
      iconColor: '#5B59EC',
      bgColor: '#FFFFFF',
      sectionBg: '#FFFFFF',
    },
    {
      id: 'venture-capital',
      icon: TrendingUp,
      title: 'Venture Capital Consulting',
      subtitle: 'Driving Economic Growth Through Strategic Investment',
      description: 'We support early and growth-stage businesses in South Africa by facilitating access to venture capital and strategic investment partnerships.',
      bullets: [
        'Trusted network of reputable investors',
        'Successful funding track record across multiple sectors',
        'Post-investment business development support',
        'Experienced corporate finance experts',
      ],
      cardHeading: 'Our Investment Advantage',
      cardIntro: 'We facilitate strategic investments and venture capital access to drive long-term business growth.',
      iconColor: '#7C3AED',
      bgColor: '#FFFFFF',
      sectionBg: '#F4F7F9',
    },
    {
      id: 'public-sector',
      icon: Building2,
      title: 'Specialised Public Sector Solutions',
      subtitle: 'Expert Municipal and Government Advisory Services',
      description: 'Drawing from our extensive public sector experience, we provide comprehensive solutions specifically designed for government departments and municipalities.',
      bullets: [
        'Leadership team with MM and CFO experience',
        'GRAP certified trainers',
        'MFMA/PFMA compliance specialists',
        'Over 20 years public sector experience',
      ],
      cardHeading: 'Our Public Sector Edge',
      cardIntro: 'We deliver targeted solutions for municipalities and government entities to strengthen financial governance and compliance.',
      iconColor: '#2B3D4C',
      bgColor: '#FFFFFF',
      sectionBg: '#FFFFFF',
    },
  ];

  const allServices = [...services, ...moreServices];

  const industries = [
  {
    category: 'Public Sector Excellence',
    items: [
      'Municipalities: Local and district municipalities',
      'Government Departments: National and provincial entities',
      'State-Owned Enterprises: Parastatals and agencies',
      'SETAs and FETs: Skills development organisations',
    ],
  },
  {
    category: 'Private Sector Expertise',
    items: [
      'Small and Medium Enterprises (SMEs)',
      'Corporate Clients: Listed and unlisted companies',
      'Non-Profit Organisations',
      'Legal Practices: Trust account management',
      'Healthcare Sector: Medical practice management',
      'Professional Services Firms',
    ],
  },
];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              className="text-white" 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold leading-snug md:leading-normal lg:leading-[1.25] mb-6">
                <span className="text-cyan-400">Delivering End-to-End</span> Financial and Advisory Excellence
              </h1>
              <p className="text-xl md:text-xl text-blue-100 max-w-4xl mx-auto">
                From audit and assurance to strategy and compliance, we help organisations strengthen governance, enhance performance, and achieve measurable results.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://media.phst.co.za/file/omc/omc-services-1.jpg?w=600&h=400&fit=crop"
                alt="Accounting, Auditing and Advisory services"
                className="rounded-2xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Sections */}
      {allServices.map((service, index) => {
        const isEven = index % 2 === 0;
        return (
          <section
            key={service.id}
            id={service.id}
            className={`py-20`}
            style={{ backgroundColor: service.sectionBg }}
          >
            <div className="container-custom">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                {/* Heading spanning full width */}
                <motion.div className="mb-8">
                  <div className="flex items-start mb-6">
                    <div
                      className="inline-flex items-center justify-center rounded-xl mr-4 flex-shrink-0"
                      style={{ 
                        backgroundColor: service.iconColor,
                        width: '64px',
                        height: '64px',
                        minWidth: '64px',
                        minHeight: '64px'
                      }}
                    >
                      {React.createElement(service.icon, { className: "h-8 w-8 text-white" })}
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900">{service.title}</h2>
                      <p className="text-lg font-medium" style={{ color: '#000565' }}>{service.subtitle}</p>
                    </div>
                  </div>
                </motion.div>

                {/* Two column layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                  {isEven ? (
                    <>
                      {/* Service Offering Left */}
                      <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        viewport={{ once: true }}
                      >
                        <p className="text-lg text-gray-600 mb-6">{service.description}</p>
                        <ul className="space-y-2 text-gray-600">
                          {service.bullets.map((bullet, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>{bullet}</span>
                            </li>
                          ))}
                        </ul>
                      </motion.div>

                      {/* Why Choose OMC Card Right */}
                      <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                      >
                        <div
                          className="text-left p-6 rounded-xl shadow-lg h-full"
                          style={{
                            backgroundColor: service.bgColor,
                            borderLeft: `6px solid ${service.iconColor}`,
                            borderRadius: '12px',
                          }}
                        >
                          <h3 className="text-2xl font-bold mb-4" style={{ color: '#111827' }}>
                            {service.cardHeading}
                          </h3>
                          <p className="text-gray-600 mb-4">{service.cardIntro}</p>
                          <ul className="space-y-2">
                            {service.bullets.map((bullet, idx) => (
                              <li key={idx} className="flex items-start">
                                <CheckCircle 
                                  className="flex-shrink-0 mr-2 mt-0.5" 
                                  style={{ color: service.iconColor, width: '20px', height: '20px' }}
                                />
                                <span style={{ color: '#111827' }}>{bullet}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    </>
                  ) : (
                    <>
                      {/* Why Choose OMC Card Left */}
                      <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        viewport={{ once: true }}
                      >
                        <div
                          className="text-left p-6 rounded-xl shadow-lg h-full"
                          style={{
                            backgroundColor: service.bgColor,
                            borderLeft: `6px solid ${service.iconColor}`,
                            borderRadius: '12px',
                          }}
                        >
                          <h3 className="text-2xl font-bold mb-4" style={{ color: '#111827' }}>
                            {service.cardHeading}
                          </h3>
                          <p className="text-gray-600 mb-4">{service.cardIntro}</p>
                          <ul className="space-y-2">
                            {service.bullets.map((bullet, idx) => (
                              <li key={idx} className="flex items-start">
                                <CheckCircle 
                                  className="flex-shrink-0 mr-2 mt-0.5" 
                                  style={{ color: service.iconColor, width: '20px', height: '20px' }}
                                />
                                <span style={{ color: '#111827' }}>{bullet}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>

                      {/* Service Offering Right */}
                      <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                      >
                        <p className="text-lg text-gray-600 mb-6">{service.description}</p>
                        <ul className="space-y-2 text-gray-600">
                          {service.bullets.map((bullet, idx) => (
                            <li key={idx} className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>{bullet}</span>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    </>
                  )}
                </div>
              </motion.div>
            </div>
          </section>
        );
      })}

      {/* Industries We Serve */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our expertise spans across both public and private sectors, serving diverse industries with tailored financial solutions.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-6">{industry.category}</h3>
                <ul className="space-y-3 text-gray-600">
                  {industry.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
<section className="py-20 bg-blue-900 text-white">
  <div className="container-custom">
    <div className="max-w-4xl mx-auto text-center">
      <div>
        <h2 className="text-3xl md:text-4xl font-bold mb-6">
          Ready to Transform Your Organisation with Expert Solutions?
        </h2>
        <p className="text-xl text-blue-100 mb-8">
          Partner with our experienced team to strengthen governance, enhance performance, and achieve your strategic objectives.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="#contact#contact-form"
            className="bg-[#0273D0] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#0093D6] hover:text-white hover:border-[#0093D6] transition-colors"
          >
            Schedule a Consultation
          </a>
          <a
            href="#contact#contact-form"
            className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition-colors"
          >
            Request a Proposal
          </a>
        </div>
      </div>
    </div>
  </div>
</section>
    </div>
  );
};

export default ServicesPage;